import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Radio, Calendar, Clock, Users, ArrowRight, Archive } from 'lucide-react';
import { sessions } from '../data/sessions';

const archivedSessions = sessions.filter(s => s.status === 'archived');
const allTags = [...new Set(archivedSessions.flatMap(s => s.tags))];

const SessionsArchivePage = () => {
  const [selectedTags, setSelectedTags] = useState([]);
  const toggleTag = (tag) => setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  const filteredSessions = selectedTags.length === 0 ? archivedSessions : archivedSessions.filter(s => s.tags.some(tag => selectedTags.includes(tag)));

  return (
    <div className="min-h-screen pt-20" data-testid="sessions-archive-page">
      <section className="py-12 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container-main relative z-10">
          <div className="max-w-3xl">
            <Link to="/services/live-sessions" className="inline-flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--error)] mb-6 font-mono text-sm"><ArrowRight className="w-4 h-4 rotate-180" /> Back to Upcoming Sessions</Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 border border-[var(--text-muted)] bg-[var(--text-muted)]/10 flex items-center justify-center"><Archive className="w-8 h-8 text-[var(--text-muted)]" /></div>
              <div>
                <p className="font-mono text-sm uppercase tracking-wider text-[var(--text-muted)]">Past Events</p>
                <h1 className="text-3xl md:text-5xl font-bold text-[var(--text-main)]">Session Archive</h1>
              </div>
            </div>
            <p className="text-[var(--text-muted)] text-lg md:text-xl leading-relaxed">Browse our past live sessions. Recordings may be available upon request.</p>
          </div>
        </div>
      </section>

      {allTags.length > 0 && (
        <section className="py-8 border-y border-[var(--border)] ">
          <div className="container-main">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <span className="font-mono text-sm text-[var(--text-muted)] uppercase tracking-wider">Filter by Tag:</span>
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => (<button key={tag} onClick={() => toggleTag(tag)} className={`px-4 py-2 font-mono text-sm border transition-colors duration-200 ${selectedTags.includes(tag) ? 'border-[var(--text-muted)] bg-[var(--text-muted)]/20 text-[var(--text-main)]' : 'border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--text-muted)]/50'}`}>{tag}</button>))}
                {selectedTags.length > 0 && (<button onClick={() => setSelectedTags([])} className="px-4 py-2 font-mono text-sm border border-[var(--error)]/50 text-[var(--error)] hover:bg-[var(--error)]/10">Clear All</button>)}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="py-16">
        <div className="container-main">
          {filteredSessions.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSessions.map((session) => (
                <div key={session.id} className="bg-[var(--surface)] border border-[var(--border)] overflow-hidden opacity-75 hover:opacity-100 transition-opacity" data-testid={`archived-session-${session.id}`}>
                  <div className="p-6">
                    <span className="inline-block px-3 py-1 bg-[var(--text-muted)]/20 text-[var(--text-muted)] text-xs font-mono uppercase mb-3">Completed</span>
                    <h3 className="text-lg font-bold text-[var(--text-main)] mb-3">{session.title}</h3>
                    <p className="text-[var(--text-muted)] text-sm mb-4 line-clamp-2">{session.description}</p>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[var(--text-muted)]" /><span className="text-sm text-[var(--text-main)] font-mono">{session.date}</span></div>
                      <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[var(--text-muted)]" /><span className="text-sm text-[var(--text-main)] font-mono">{session.duration}</span></div>
                      <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[var(--text-muted)]" /><span className="text-sm text-[var(--text-main)] font-mono">{session.attendees} attended</span></div>
                      <div className="flex items-center gap-2"><Radio className="w-4 h-4 text-[var(--text-muted)]" /><span className="text-sm text-[var(--text-main)] font-mono">{session.host}</span></div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {session.tags.map((tag, idx) => (<span key={idx} className="px-2 py-1 bg-[var(--surface-highlight)] border border-[var(--border)] text-xs text-[var(--text-muted)] font-mono">{tag}</span>))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Archive className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
              <p className="text-[var(--text-muted)] text-lg">No archived sessions found.</p>
              <Link to="/services/live-sessions" className="mt-4 inline-block text-[var(--error)] font-mono hover:underline">View Upcoming Sessions</Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SessionsArchivePage;
